import { useEffect, useState, useMemo, type CSSProperties } from "react";
import {
  getCocktailMappingCoverage,
  listDrinksForMapping,
  listSpiritIngredientsForMapping,
  listPourItemsForMapping,
  searchSquareItemsForMapping,
  upsertCocktailMap,
  upsertIngredientMap,
  deactivateCocktailMap,
  deactivateIngredientMap,
  type CocktailCoverageResponse,
  type DrinkMappingItem,
  type IngredientMappingItem,
  type PourMenuItem,
  type SquareSearchItem,
} from "../../lib/api";
import RecipeStudioTabs from "../../components/admin/RecipeStudioTabs";

export default function AdminCocktailMapping() {
  const [tab, setTab] = useState<"drinks" | "ingredients">("drinks");
  const [coverage, setCoverage] = useState<CocktailCoverageResponse | null>(null);
  const [drinks, setDrinks] = useState<DrinkMappingItem[]>([]);
  const [ingredients, setIngredients] = useState<IngredientMappingItem[]>([]);
  const [pours, setPours] = useState<PourMenuItem[]>([]);
  const [squareItems, setSquareItems] = useState<SquareSearchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState("");

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"drink" | "ingredient">("drink");
  const [targetId, setTargetId] = useState("");
  const [targetName, setTargetName] = useState("");
  const [modalQuery, setModalQuery] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  // Messages
  const [toastMsg, setToastMsg] = useState<{ text: string; type: "ok" | "err" } | null>(null);

  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 3500);
  };

  // Escape listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalOpen) setModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [covRes, drinksRes, ingRes, poursRes] = await Promise.all([
        getCocktailMappingCoverage().catch((e) => {
          console.warn("Cocktail mapping coverage warning:", e);
          return null;
        }),
        listDrinksForMapping(),
        listSpiritIngredientsForMapping(),
        listPourItemsForMapping(),
      ]);
      if (covRes) setCoverage(covRes);
      setDrinks(drinksRes.drinks || []);
      setIngredients(ingRes.ingredients || []);
      setPours(poursRes.pours || []);
    } catch (e: any) {
      showToast(e.message || "Failed to load mapping data", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtered lists
  const filteredDrinks = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    if (!q) return drinks;
    return drinks.filter((d) => d.drink_name.toLowerCase().includes(q));
  }, [drinks, searchFilter]);

  const filteredIngredients = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    if (!q) return ingredients;
    return ingredients.filter((i) => i.ingredient_name.toLowerCase().includes(q));
  }, [ingredients, searchFilter]);

  // Modal actions
  const openAssignDrinkModal = async (drinkId: string, drinkName: string) => {
    setModalType("drink");
    setTargetId(drinkId);
    setTargetName(drinkName);
    setModalQuery("");
    setModalOpen(true);

    if (squareItems.length === 0) {
      setModalLoading(true);
      try {
        const res = await searchSquareItemsForMapping();
        setSquareItems(res.items || []);
      } catch (e: any) {
        showToast(e.message || "Failed to fetch Square items", "err");
      } finally {
        setModalLoading(false);
      }
    }
  };

  const openAssignIngredientModal = async (ingredientId: string, ingredientName: string) => {
    setModalType("ingredient");
    setTargetId(ingredientId);
    setTargetName(ingredientName);
    setModalQuery("");
    setModalOpen(true);

    if (pours.length === 0) {
      setModalLoading(true);
      try {
        const res = await listPourItemsForMapping();
        setPours(res.pours || []);
      } catch (e: any) {
        showToast(e.message || "Failed to fetch pour bottles", "err");
      } finally {
        setModalLoading(false);
      }
    }
  };

  const handleAssignDrink = async (squareItemId: string) => {
    setActionBusy(true);
    try {
      await upsertCocktailMap({
        drink_id: targetId,
        drink_name: targetName,
        square_item_id: squareItemId,
      });
      showToast(`Mapped "${targetName}" to Square item`);
      setModalOpen(false);
      await loadData();
    } catch (e: any) {
      showToast(e.message || "Failed to assign Square item", "err");
    } finally {
      setActionBusy(false);
    }
  };

  const handleAssignIngredient = async (menuItemId: string) => {
    setActionBusy(true);
    try {
      await upsertIngredientMap({
        ingredient_id: targetId,
        ingredient_name: targetName,
        menu_item_id: menuItemId,
      });
      showToast(`Mapped "${targetName}" to bottle pour`);
      setModalOpen(false);
      await loadData();
    } catch (e: any) {
      showToast(e.message || "Failed to assign bottle pour", "err");
    } finally {
      setActionBusy(false);
    }
  };

  const handleUnmapDrink = async (drinkId: string, drinkName: string) => {
    if (!window.confirm(`Unmap "${drinkName}" from Square?`)) return;
    try {
      await deactivateCocktailMap(drinkId);
      showToast(`Unmapped "${drinkName}"`);
      await loadData();
    } catch (e: any) {
      showToast(e.message || "Failed to unmap drink", "err");
    }
  };

  const handleUnmapIngredient = async (ingredientId: string, ingredientName: string) => {
    if (!window.confirm(`Unmap "${ingredientName}" from bottle pour?`)) return;
    try {
      await deactivateIngredientMap(ingredientId);
      showToast(`Unmapped "${ingredientName}"`);
      await loadData();
    } catch (e: any) {
      showToast(e.message || "Failed to unmap ingredient", "err");
    }
  };

  // Modal filtering
  const filteredModalSquareItems = useMemo(() => {
    const q = modalQuery.trim().toLowerCase();
    if (!q) return squareItems.slice(0, 50);
    return squareItems.filter((i) => i.name.toLowerCase().includes(q)).slice(0, 50);
  }, [squareItems, modalQuery]);

  const filteredModalPours = useMemo(() => {
    const q = modalQuery.trim().toLowerCase();
    if (!q) return pours.slice(0, 50);
    return pours.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 50);
  }, [pours, modalQuery]);

  return (
    <div style={styles.container}>
      <RecipeStudioTabs activeTab="mapping" badgeCounts={{ mapping: drinks.length }} />
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Cocktail Pour Mapping</h1>
          <p style={styles.subtitle}>
            Link Station cocktails to Square POS catalog items and map spirit ingredients to inventory bottle pours.
          </p>
        </div>
      </header>

      {toastMsg && (
        <div
          style={{
            ...styles.toast,
            background: toastMsg.type === "ok" ? "rgba(39, 174, 96, 0.15)" : "rgba(224, 80, 64, 0.15)",
            borderColor: toastMsg.type === "ok" ? "#27ae60" : "var(--red)",
            color: toastMsg.type === "ok" ? "#86efac" : "var(--red)",
          }}
        >
          {toastMsg.text}
        </div>
      )}

      {/* Coverage Status Card */}
      <div style={styles.coverageCard}>
        <div style={styles.covHeader}>Deduction Coverage Status</div>
        <div style={styles.covStatsRow}>
          <div style={styles.covStat}>
            <span style={styles.covNum}>
              {coverage ? coverage.unmapped_drinks : "—"}
            </span>
            <span style={styles.covLabel}>Drinks Unmapped to Square</span>
          </div>
          <div style={styles.covStat}>
            <span style={styles.covNum}>
              {coverage ? coverage.unmapped_spirit_ingredients : "—"}
            </span>
            <span style={styles.covLabel}>Spirit Ingredients Unmapped</span>
          </div>
        </div>

        {coverage?.recent_gaps && coverage.recent_gaps.length > 0 && (
          <div style={styles.gapsWrap}>
            <div style={styles.gapsLabel}>Recent Deduction Gaps:</div>
            <div style={styles.gapsList}>
              {coverage.recent_gaps.slice(0, 5).map((g, idx) => (
                <div key={idx} style={styles.gapRow}>
                  <span style={styles.gapTime}>
                    {new Date(g.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span>{g.reason || g.metadata?.gap_type || "Unmapped ingredient deduction skipped"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toolbar & Tabs */}
      <div style={styles.tabsRow}>
        <button
          style={{
            ...styles.tabBtn,
            ...(tab === "drinks" ? styles.tabBtnActive : {}),
          }}
          onClick={() => {
            setTab("drinks");
            setSearchFilter("");
          }}
        >
          Drinks → Square POS ({drinks.length})
        </button>
        <button
          style={{
            ...styles.tabBtn,
            ...(tab === "ingredients" ? styles.tabBtnActive : {}),
          }}
          onClick={() => {
            setTab("ingredients");
            setSearchFilter("");
          }}
        >
          Ingredients → Bottle Pours ({ingredients.length})
        </button>
      </div>

      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder={tab === "drinks" ? "Search cocktails..." : "Search spirit ingredients..."}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          style={styles.searchInput}
        />
        <button style={styles.refreshBtn} onClick={loadData}>
          Refresh
        </button>
      </div>

      {/* Main List */}
      {loading ? (
        <div style={styles.loader}>Loading mapping ledger…</div>
      ) : tab === "drinks" ? (
        <div style={styles.listWrap}>
          {filteredDrinks.map((d) => {
            const isMapped = !!d.square_item_id;
            return (
              <div
                key={d.drink_id}
                style={{
                  ...styles.row,
                  borderLeft: isMapped ? "3px solid #27ae60" : "3px solid var(--red)",
                }}
              >
                <div style={{ flex: "1 1 200px", minWidth: 0, overflowWrap: "anywhere" }}>
                  <div style={styles.rowTitle}>{d.drink_name}</div>
                  <div style={styles.rowSub}>
                    {isMapped
                      ? `Mapped to Square Item ${d.square_item_id}`
                      : "Not mapped — POS sales will not deduct ingredient stock"}
                  </div>
                </div>
                <div style={styles.rowActions}>
                  <button
                    style={isMapped ? styles.btnChange : styles.btnMap}
                    onClick={() => openAssignDrinkModal(d.drink_id, d.drink_name)}
                  >
                    {isMapped ? "Change" : "Map to Square"}
                  </button>
                  {isMapped && (
                    <button
                      style={styles.btnUnmap}
                      onClick={() => handleUnmapDrink(d.drink_id, d.drink_name)}
                    >
                      Unmap
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filteredDrinks.length === 0 && (
            <div style={styles.emptyState}>No cocktail recipes matched your search.</div>
          )}
        </div>
      ) : (
        <div style={styles.listWrap}>
          {filteredIngredients.map((i) => {
            const isMapped = !!i.menu_item_id;
            const mappedPour = pours.find((p) => p.id === i.menu_item_id);
            return (
              <div
                key={i.ingredient_id}
                style={{
                  ...styles.row,
                  borderLeft: isMapped ? "3px solid #27ae60" : "3px solid var(--red)",
                }}
              >
                <div style={{ flex: "1 1 200px", minWidth: 0, overflowWrap: "anywhere" }}>
                  <div style={styles.rowTitle}>{i.ingredient_name}</div>
                  <div style={styles.rowSub}>
                    {isMapped
                      ? `Mapped → ${mappedPour ? mappedPour.name : i.menu_item_id}`
                      : "Not mapped to a specific bottle pour"}
                  </div>
                </div>
                <div style={styles.rowActions}>
                  <button
                    style={isMapped ? styles.btnChange : styles.btnMap}
                    onClick={() => openAssignIngredientModal(i.ingredient_id, i.ingredient_name)}
                  >
                    {isMapped ? "Change" : "Map to Bottle"}
                  </button>
                  {isMapped && (
                    <button
                      style={styles.btnUnmap}
                      onClick={() => handleUnmapIngredient(i.ingredient_id, i.ingredient_name)}
                    >
                      Unmap
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filteredIngredients.length === 0 && (
            <div style={styles.emptyState}>No spirit ingredients matched your search.</div>
          )}
        </div>
      )}

      {/* Assignment Modal */}
      {modalOpen && (
        <div style={styles.modalBackdrop} onClick={() => setModalOpen(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {modalType === "drink"
                  ? `Map "${targetName}" to Square Catalog Item`
                  : `Map "${targetName}" to Bottle Pour`}
              </h2>
              <button style={styles.modalClose} onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </div>

            <input
              type="text"
              placeholder="Filter options..."
              value={modalQuery}
              onChange={(e) => setModalQuery(e.target.value)}
              style={styles.modalSearch}
              autoFocus
            />

            <div style={styles.modalList}>
              {modalLoading ? (
                <div style={styles.modalLoading}>Loading options…</div>
              ) : modalType === "drink" ? (
                filteredModalSquareItems.map((item) => (
                  <div
                    key={item.square_item_id}
                    style={styles.modalOption}
                    onClick={() => !actionBusy && handleAssignDrink(item.square_item_id)}
                  >
                    <div style={styles.optionTitle}>{item.name}</div>
                    <div style={styles.optionSub}>Square ID: {item.square_item_id}</div>
                  </div>
                ))
              ) : (
                filteredModalPours.map((pour) => (
                  <div
                    key={pour.id}
                    style={styles.modalOption}
                    onClick={() => !actionBusy && handleAssignIngredient(pour.id)}
                  >
                    <div style={styles.optionTitle}>{pour.name}</div>
                    <div style={styles.optionSub}>
                      Status: {pour.status} {pour.pours_remaining != null ? `· ${pour.pours_remaining} pours left` : ""}
                    </div>
                  </div>
                ))
              )}
              {modalType === "drink" && filteredModalSquareItems.length === 0 && !modalLoading && (
                <div style={styles.modalEmpty}>No matching Square cocktail items found.</div>
              )}
              {modalType === "ingredient" && filteredModalPours.length === 0 && !modalLoading && (
                <div style={styles.modalEmpty}>No matching bottle pours found.</div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.modalCancelBtn}
                onClick={() => setModalOpen(false)}
                disabled={actionBusy}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 20px 60px",
    maxWidth: 1100,
    margin: "0 auto",
    color: "var(--text, #F7F5F0)",
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  toast: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid",
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 600,
  },
  coverageCard: {
    background: "rgba(249, 175, 54, 0.05)",
    border: "1px solid rgba(249, 175, 54, 0.2)",
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  covHeader: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--gold, #F9AF36)",
    marginBottom: 12,
  },
  covStatsRow: {
    display: "flex",
    gap: 32,
    flexWrap: "wrap",
  },
  covStat: {
    display: "flex",
    flexDirection: "column",
  },
  covNum: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    fontWeight: "bold",
    color: "var(--gold, #F9AF36)",
  },
  covLabel: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  gapsWrap: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: "1px solid var(--border, #231D19)",
  },
  gapsLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "var(--muted, #A39D98)",
    marginBottom: 6,
  },
  gapsList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
    color: "var(--text, #F7F5F0)",
  },
  gapRow: {
    display: "flex",
    gap: 8,
  },
  gapTime: {
    color: "var(--muted, #A39D98)",
    fontFamily: "monospace",
  },
  tabsRow: {
    display: "flex",
    gap: 8,
    borderBottom: "1px solid var(--border, #231D19)",
    marginBottom: 16,
  },
  tabBtn: {
    background: "transparent",
    border: 0,
    borderBottom: "2px solid transparent",
    color: "var(--muted, #A39D98)",
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  tabBtnActive: {
    color: "var(--gold, #F9AF36)",
    borderColor: "var(--gold, #F9AF36)",
  },
  toolbar: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
    padding: "10px 14px",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    color: "var(--text, #F7F5F0)",
    fontSize: 14,
    outline: "none",
  },
  refreshBtn: {
    padding: "10px 16px",
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    color: "var(--muted, #A39D98)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  listWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    display: "flex",
    flexWrap: "wrap", // Change / Unmap drop under a long drink name instead of widening the page
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    gap: 16,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text, #F7F5F0)",
  },
  rowSub: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
    marginTop: 2,
  },
  rowActions: {
    display: "flex",
    gap: 8,
  },
  btnMap: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  btnChange: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--text, #F7F5F0)",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnUnmap: {
    background: "transparent",
    border: "1px solid rgba(224, 80, 64, 0.3)",
    color: "var(--red, #FF8A7A)",
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  loader: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #A39D98)",
    fontSize: 14,
  },
  emptyState: {
    textAlign: "center",
    padding: 32,
    color: "var(--muted, #A39D98)",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    fontSize: 13,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 100,
  },
  modalCard: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    width: "100%",
    maxWidth: 520,
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    padding: 20,
    boxShadow: "0 16px 40px rgba(0, 0, 0, 0.7)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 16,
    margin: 0,
    color: "var(--text, #F7F5F0)",
  },
  modalClose: {
    background: "transparent",
    border: 0,
    color: "var(--muted, #A39D98)",
    fontSize: 22,
    cursor: "pointer",
    lineHeight: 1,
  },
  modalSearch: {
    padding: "10px 12px",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    marginBottom: 12,
    outline: "none",
  },
  modalList: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minHeight: 150,
  },
  modalOption: {
    padding: "10px 12px",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    cursor: "pointer",
    transition: "border-color 0.15s ease",
  },
  optionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--text, #F7F5F0)",
  },
  optionSub: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
    marginTop: 2,
  },
  modalLoading: {
    textAlign: "center",
    padding: 24,
    color: "var(--muted, #A39D98)",
    fontSize: 13,
  },
  modalEmpty: {
    textAlign: "center",
    padding: 24,
    color: "var(--muted, #A39D98)",
    fontSize: 13,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 14,
    paddingTop: 10,
    borderTop: "1px solid var(--border, #231D19)",
  },
  modalCancelBtn: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
};
